// src/utils/salesPdf.js
// Builds a Sales Audit PDF for one visitor (original details + submitted
// follow-up answers). Shared by the admin Visitors panel; the standalone
// Customer/sales-audit.html page keeps its own copy of this logic (it can't
// import a module).
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { SALES_FIELDS, ORIGINAL_FIELDS, parseSalesDetails, isFieldVisible } from './salesFields';

const GOLD = [184, 134, 46];
const DARK = [26, 26, 26];

function val(v) {
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

// Build (and download) the Sales Audit PDF. Returns the filename used.
export function downloadSalesAuditPdf(visitor, salesDetailsRaw) {
  const sd = parseSalesDetails(salesDetailsRaw ?? visitor.sales_details);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();

  // ── Header band ──
  doc.setFillColor(...DARK);
  doc.rect(0, 0, pageW, 60, 'F');
  doc.setTextColor(...GOLD);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.text('Guru Punvaanii', 40, 30);
  doc.setFontSize(9);
  doc.setTextColor(212, 168, 75);
  doc.text('SALES AUDIT — CUSTOMER ENQUIRY', 40, 46);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.text(`Ref: ${val(visitor.ref_number)}`, pageW - 40, 34, { align: 'right' });

  let y = 80;
  doc.setTextColor(...DARK);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Visitor Details', 40, y);
  y += 8;

  autoTable(doc, {
    startY: y,
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4, textColor: [50, 50, 50] },
    columnStyles: { 0: { cellWidth: 150, fontStyle: 'bold', fillColor: [253, 246, 227] } },
    body: ORIGINAL_FIELDS.map(f => [f.label, val(visitor[f.key])]),
    margin: { left: 40, right: 40 },
  });

  // ── Sales Audit answers, grouped by section ──
  let group = null;
  const flush = () => {
    if (!group) return;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...GOLD);
    const startY = doc.lastAutoTable.finalY + 18;
    doc.text(group.title, 40, startY);
    autoTable(doc, {
      startY: startY + 6,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 4, textColor: [50, 50, 50] },
      columnStyles: { 0: { cellWidth: 220, fontStyle: 'bold', fillColor: [249, 244, 235] } },
      body: group.rows,
      margin: { left: 40, right: 40 },
    });
    group = null;
  };

  SALES_FIELDS.forEach(f => {
    if (f.section) { flush(); group = { title: f.section, rows: [] }; return; }
    // Conditional field whose condition wasn't met — leave it off the PDF.
    if (!isFieldVisible(f, sd)) return;
    if (!group) group = { title: 'Details', rows: [] };
    group.rows.push([f.label, val(sd[f.key])]);
  });
  flush();

  // ── Footer with audit trail ──
  const finalY = doc.lastAutoTable.finalY + 20;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(120, 120, 120);
  const by = visitor.sales_updated_by || visitor.accepted_by_name || '—';
  const on = visitor.sales_updated_at ? String(visitor.sales_updated_at).split(' ')[0] : new Date().toISOString().slice(0, 10);
  doc.text(`Filled by ${by} on ${on}  ·  Generated ${new Date().toLocaleString('en-IN')}`, 40, finalY);

  const fname = `SalesAudit_${(visitor.ref_number || visitor.id || 'record')}.pdf`;
  doc.save(fname);
  return fname;
}
